/**
 * OnboardingPaymentPage — RC 1.35.E Phase B (Stripe Checkout).
 *
 * Replaces the RC 1.32 stub. Flow:
 *
 *   1. Page loads → fetch `/api/account/plan` to confirm there is a
 *      pending paid plan.
 *   2. User clicks "Pay with Stripe" → POST
 *      `/api/payments/checkout/plan` with the pending plan tuple.
 *      Backend returns `{checkout_url, session_id}`; frontend
 *      redirects to Stripe.
 *   3. After payment, Stripe redirects back to
 *      `/onboarding/payment?session_id=cs_test_...`.
 *      The page polls
 *      `/api/payments/checkout/status/{session_id}` every 2 s for
 *      up to 60 s. When `finalised:true` AND
 *      `/api/account/plan` reports `status=active`, redirect to
 *      `/dashboard`.
 *   4. If Stripe redirects with `?cancelled=1`, show a toast and let
 *      the user retry.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2, CreditCard, ShieldCheck, ArrowLeft, X } from 'lucide-react';
import { isStripeTestMode } from '@/lib/stripeMode';
import LegalFooter from '@/components/LegalFooter';

const PLAN_LABEL = { focus: 'Focus', flow: 'Flow', base: 'Base' };
const PERIOD_LABEL = { monthly: 'Monthly', annual: 'Annual' };

const POLL_MS = 2000;
const POLL_LIMIT = 30; // 60s total

export default function OnboardingPaymentPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [polling, setPolling] = useState(false);
  const cancelledRef = useRef(false);

  // RC 1.33.D — origin for Cancel navigation. Set by
  // OnboardingPlanPage when the user was editing an existing plan
  // from /account.
  const returnTo = location.state?.from || null;

  // Bootstrap. If the URL carries ?session_id we begin polling
  // immediately. Otherwise we just confirm there's a pending paid
  // plan.
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const params = new URLSearchParams(location.search);
        const sessionId = params.get('session_id');
        const cancelled = params.get('cancelled') === '1';

        if (cancelled) {
          cancelledRef.current = true;
          toast.error('Checkout cancelled. You can try again whenever you are ready.');
        }

        const p = await apiFetch('/api/account/plan');
        if (!mounted) return;
        setPlan(p);

        if (sessionId) {
          await pollUntilFinalised(sessionId, mounted);
        }
      } catch {
        // fall through; action button will also error if the API is down.
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Honest gate: if the user lands here without a pending paid plan,
  // bounce them back to plan selection.
  useEffect(() => {
    if (loading || polling || redirecting || cancelledRef.current) return;
    if (!plan) return;
    const code = plan.plan_code;
    const params = new URLSearchParams(location.search);
    const hasReturnSession = !!params.get('session_id');
    const needsPayment = (code === 'focus' || code === 'flow')
      && plan.status === 'pending';
    if (!needsPayment && !hasReturnSession) {
      navigate('/onboarding/plan', { replace: true });
    }
  }, [loading, plan, polling, redirecting, location.search, navigate]);

  const pollUntilFinalised = async (sessionId, mountedFlag) => {
    setPolling(true);
    try {
      for (let i = 0; i < POLL_LIMIT; i++) {
        if (!mountedFlag) return;
        let resp;
        try {
          resp = await apiFetch(`/api/payments/checkout/status/${sessionId}`);
        } catch (err) {
          // 404 → session not ours. Surface and stop.
          toast.error(err?.message || 'Could not verify payment status.');
          return;
        }
        if (resp?.finalised || resp?.status === 'finalised') {
          // Refresh plan and redirect when entitlement is active.
          const p = await apiFetch('/api/account/plan');
          if (p?.status === 'active') {
            toast.success(`${PLAN_LABEL[p.plan_code] || 'Plan'} activated.`);
            setRedirecting(true);
            // RC 1.44.O1.B — Paid activation routes through the
            // forced re-auth handoff instead of /dashboard so the
            // new entitlement hydrates from a fresh session and any
            // pre-activation Stripe state is left behind cleanly.
            navigate('/plan-activated', { replace: true });
            return;
          }
          // Stripe finalised the session but the entitlement webhook
          // hasn't landed yet — keep polling.
        }
        await new Promise((r) => setTimeout(r, POLL_MS));
      }
      toast.error('Activation is taking longer than expected. Please refresh in a moment.');
    } finally {
      setPolling(false);
    }
  };

  const handlePay = async () => {
    if (!plan?.plan_code || !plan?.billing_period) return;
    setRedirecting(true);
    try {
      const res = await apiFetch('/api/payments/checkout/plan', {
        method: 'POST',
        body: JSON.stringify({
          plan_code: plan.plan_code,
          billing_period: plan.billing_period,
        }),
      });
      if (!res?.checkout_url) {
        throw new Error('Stripe did not return a checkout URL.');
      }
      window.location.assign(res.checkout_url);
    } catch (err) {
      toast.error(err?.message || 'Could not start Stripe Checkout. Please try again.');
      setRedirecting(false);
    }
  };

  if (loading || (!plan && !cancelledRef.current)) {
    return (
      <div className="min-h-screen bg-ndor-canvas flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-ndor-ink-muted" />
      </div>
    );
  }

  if (polling) {
    return (
      <div
        className="min-h-screen bg-ndor-canvas flex items-center justify-center"
        data-testid="onboarding-payment-polling"
      >
        <div className="text-center space-y-3">
          <Loader2 className="h-8 w-8 animate-spin text-ndor-gold mx-auto" />
          <p className="text-sm text-ndor-ink-secondary">
            Activating your plan…
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-ndor-canvas flex flex-col items-center px-4 py-16"
      data-testid="onboarding-payment-page"
    >
      <div className="max-w-lg w-full space-y-6 flex-1">
        <header className="text-center space-y-2">
          <p className="text-[11px] uppercase tracking-[0.22em] text-ndor-gold font-mono-ui">
            Activate your plan
          </p>
          <h1
            className="text-3xl font-serif-display font-semibold text-ndor-ink tracking-tight"
            data-testid="onboarding-payment-title"
          >
            Almost there
          </h1>
          <p className="text-sm text-ndor-ink-secondary">
            You'll complete payment securely on Stripe. We never see or store your card details.
          </p>
        </header>

        <Card className="border-ndor-edge bg-ndor-surface">
          <CardContent className="p-6 space-y-5">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.14em] text-ndor-ink-muted font-mono-ui">
                  Selected plan
                </p>
                <p
                  className="text-2xl font-serif-display font-semibold text-ndor-ink"
                  data-testid="onboarding-payment-plan-name"
                >
                  {PLAN_LABEL[plan?.plan_code] || plan?.plan_code}
                </p>
                <p className="text-sm text-ndor-ink-secondary">
                  {PERIOD_LABEL[plan?.billing_period] || 'Monthly'} billing cycle
                </p>
              </div>
              <CreditCard className="h-10 w-10 text-ndor-ink-muted" />
            </div>

            <div
              className="flex items-start gap-2 rounded-md border border-ndor-edge bg-ndor-surface-alt/40 p-3 text-xs text-ndor-ink-secondary"
              data-testid="onboarding-payment-stripe-note"
            >
              <ShieldCheck className="h-4 w-4 shrink-0 mt-[2px] text-ndor-gold" />
              {isStripeTestMode() ? (
                <span>
                  Secure Stripe Checkout — TEST MODE. Use Stripe's test card
                  <span className="font-mono-ui mx-1">4242 4242 4242 4242</span>
                  with any future expiry and any 3-digit CVC. No real money will move.
                </span>
              ) : (
                <span>
                  Secure billing powered by Stripe. We never see or store your
                  card details. Your subscription is governed by your plan terms
                  and our <a href="/refund" className="text-ndor-ink underline hover:text-ndor-ink/80">Refund Policy</a>.
                </span>
              )}
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-between sm:items-center">
              <div className="flex flex-col sm:flex-row gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    toast.message('Returning to plans…');
                    navigate('/onboarding/plan', {
                      replace: true,
                      state: returnTo ? { from: returnTo } : undefined,
                    });
                  }}
                  data-testid="onboarding-payment-back"
                >
                  <ArrowLeft className="h-4 w-4 mr-1.5" />
                  Back to plans
                </Button>
                {/* RC 1.35.E.UI Fix C — only render the Cancel button
                    when there is a meaningful, distinct destination
                    (the user came from /account → "Change plan").
                    Without `returnTo`, "Cancel" and "Back to plans"
                    were doing the same thing and confused first-time
                    onboarding users. */}
                {returnTo && (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      toast.message('Cancelled — keeping your current plan.');
                      navigate(returnTo, { replace: true });
                    }}
                    data-testid="onboarding-payment-cancel"
                    className="text-ndor-ink-muted hover:text-ndor-ink"
                  >
                    <X className="h-4 w-4 mr-1.5" />
                    Cancel and keep current plan
                  </Button>
                )}
              </div>
              <Button
                onClick={handlePay}
                disabled={redirecting || !plan?.plan_code || !plan?.billing_period}
                data-testid="onboarding-payment-confirm"
                className="bg-ndor-primary hover:bg-ndor-primary-hover text-ndor-primary-foreground"
              >
                {redirecting
                  ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  : null}
                Pay with Stripe
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
      <LegalFooter variant="auth" />
    </div>
  );
}
